// Phase 1 — browser-facing service-capture UI helpers (CommonJS, dual-use).
// Imported by src/App.jsx (admin attempt capture + history) and unit-tested by
// node. Pure: no network/DOM. The browser bundle cannot import from
// netlify/functions/lib, so the small overlaps (outcomeLabel / isCaptureEnabled)
// are intentionally mirrored from netlify/functions/lib/serviceData.js.

const SI = require("./serviceIntake");   // one vocabulary for both the form and the row
const FS = require("./fieldService");     // field outcome vocabulary
const AW = require("./attemptWording"); // "Other" observation text

const MANNERS = ["personal", "substitute", "posting", "mail"];

const OUTCOME_LABELS = {
  served_personal: "Served — Personal",
  served_substitute: "Served — Substitute",
  served_posting: "Served — Posted",
  drop_served: "Served — Drop (Refused in Presence)",
  no_answer: "No Answer",
  refused: "Refused",
  wrong_address: "Moved / Does Not Live Here",
  vacant: "Vacant Property",
  business_closed: "Business Closed",
  unable_to_locate: "Unable to Locate",
  evaded: "Evaded Service",
  inaccessible: "Unable to Access",
  other: "Other",
};
function outcomeLabel(outcome) {
  return OUTCOME_LABELS[outcome] || String(outcome || "").replace(/_/g, " ");
}

// Whether structured field capture is enabled (Phase 0 feature flag).
function isCaptureEnabled(config) {
  return !!(config && config.feature_flags && config.feature_flags.field_capture === true);
}

// Persisted attempts → display rows (sorted by attempt number ascending).
function buildAttemptTimeline(attempts) {
  return (Array.isArray(attempts) ? [...attempts] : [])
    .sort((a, b) => (Number(a.attempt_number) || 0) - (Number(b.attempt_number) || 0))
    .map((a) => ({
      number: a.attempt_number,
      date: a.attempt_date || a.date || null,
      time: a.attempt_time || a.time || null,
      outcome: a.outcome,
      label: outcomeLabel(a.outcome),
    }));
}

// Duplicate-submission guard + basic validity: allow a submit only with a service
// date and when no save is already in flight.
function canSubmitAttempt(form, opts) {
  const saving = !!(opts && opts.saving);
  const hasDate = !!(form && form.date);
  return hasDate && !saving;
}

// Map the existing admin note/service-capture form to a Phase 1 attempt input for
// POST /log-attempt. client_summary is intentionally left null — raw field notes
// are never auto-published to the client.
//
// This mapping used to forward only date, time, manner, outcome and the free-text
// note, so every other fact the process server entered (who was present, how they
// identified themselves, accepted/refused, relationship, physical description,
// statements, observations, mailing details, GPS) lived in React state and was
// lost on reload — a declaration could not be regenerated from the record. All
// of those now travel with the attempt; migration 014 adds the columns.
//
// Blank is blank: an untouched field maps to null rather than "" so the DB stores
// NULL and nothing is invented on the admin's behalf.
const blank = (v) => {
  if (v == null) return null;
  const s = String(v).trim();
  return s === "" ? null : s;
};
function mapNoteFormToAttempt(noteForm, jobNumber, submissionToken) {
  const f = noteForm || {};
  let manner = MANNERS.includes(f.serviceType) ? f.serviceType : "personal";
  let outcome;
  if (f.outcome === "served_posting") {
    // Court-ordered posting (step-by-step entry → Served → Posted).
    outcome = "served_posting";
    manner = "posting";
  } else if (FS.FIELD_OUTCOME_VALUES.includes(f.outcome)) {
    // The outcome the server picked explicitly at the door.
    outcome = f.outcome;
    manner = FS.mannerForOutcome(outcome, manner);
  } else if (f.status === "served") {
    outcome = f.serviceType === "substitute" ? "served_substitute" : "served_personal";
  } else if (f.status === "nonservice" || f.status === "non_service") {
    // The form's option value is "non_service"; only "nonservice" used to match,
    // so a Non-Service entry silently saved as "no_answer".
    outcome = "unable_to_locate";
  } else if (/^refused/i.test(String(f.serviceAccepted || ""))) {
    // The option text is "Refused service — …", never exactly "refused".
    outcome = "refused";
  } else {
    outcome = "no_answer";
  }
  const gps = f.gps && typeof f.gps === "object" ? f.gps : null;
  // Special features is a MULTI-select now: a person can wear glasses and have a
  // beard, which the old single string could not say. Legacy free text is still
  // accepted and stored as a one-entry list so older forms keep working.
  const featureList = SI.normalizeFeatures(f.features);
  const legacyFeature = Array.isArray(f.features) ? null : blank(f.features);
  return {
    order_ref: jobNumber,
    target_id: "primary",
    // The selected service address, so the attempt is tied to WHERE it happened
    // instead of leaving address_id NULL and rendering "[ADDRESS MISSING]" in
    // every diligence line. Resolved server-side to the target's primary address
    // when the form has not selected one.
    address_id: blank(f.addressId),
    manner_attempted: manner,
    outcome,
    attempt_date: blank(f.date),
    attempt_time: blank(f.time),
    // A different address typed on the last screen is kept with the notes.
    internal_notes: blank([String(f.desc || "").trim(), String(f.locationOverride || "").trim() ? `Address of this activity: ${String(f.locationOverride).trim()}` : ""].filter(Boolean).join("\n")),
    capture_method: "field_captured",
    origin: "portal",
    submission_token: submissionToken || null,

    // ---- Facts observed at the door ----
    location_type: blank(f.locationType),
    person_contacted_category: blank(f.personPresent),
    person_contacted_detail: blank(f.personPresentDetail),
    // WHICH relative, when a family member answered. Only meaningful for that
    // category, so it is not carried for any other.
    person_contacted_family_relation: SI.needsFamilyRelation(f.personPresent) ? blank(f.personPresentFamily) : null,
    identification_method: blank(f.identification),
    service_acceptance: blank(f.serviceAccepted),
    relationship: blank(f.relationship),

    // ---- Physical description (only what was entered) ----
    gender: blank(f.gender),
    approximate_age: blank(f.age),
    ethnicity: blank(f.ethnicity),
    height: blank(f.height),
    weight: blank(f.weight),
    hair_color: blank(f.hair),
    special_features: featureList || (legacyFeature == null ? null : [legacyFeature]),
    special_features_other: SI.needsFeatureOtherText(f.features) ? blank(f.featuresOther) : null,

    // ---- Statements, observations, other contact ----
    statement_made: blank(f.personSaid),
    // "Other" saves the text the server typed in.
    address_observations: blank(AW.observationValue(f.observed, f.observedOther)),
    other_contact_category: blank(f.otherContact),
    other_contact_detail: blank(f.otherContactDetail),

    // ---- Mailing (substituted-service follow-up) ----
    mailed_on: blank(f.mailingDate),
    mailing_method: blank(f.mailingMethod),
    tracking_number: blank(f.mailingTracking),

    // ---- GPS captured at the attempt ----
    // These are the EXISTING production service_attempts columns. No second,
    // differently-named GPS set is ever written.
    // gps_source / gps_unavailable_reason are carried when the form supplies
    // them and stay null otherwise — nothing is invented to fill them.
    gps_lat: gps && Number.isFinite(Number(gps.lat)) ? Number(gps.lat) : null,
    gps_lng: gps && Number.isFinite(Number(gps.lng)) ? Number(gps.lng) : null,
    gps_accuracy_m: gps && Number.isFinite(Number(gps.accuracy)) ? Math.round(Number(gps.accuracy)) : null,
    gps_source: blank(gps && gps.source) || blank(f.gpsSource),
    gps_unavailable_reason: blank(f.gpsUnavailableReason),
  };
}

module.exports = {
  MANNERS,
  outcomeLabel,
  isCaptureEnabled,
  buildAttemptTimeline,
  canSubmitAttempt,
  mapNoteFormToAttempt,
};
